import {
  Checkbox,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  ListSubheader,
  Stack,
  Typography
} from '@mui/material'
import { Fragment } from 'react'

import {
  getLaborName,
  ServiceItem
} from '../../hooks/useTransformServiceItems/useTransformServiceItems'
import {
  displayPartsTechPartName,
  displayStandAlonePartName
} from '../../models/parts'
import { ServiceEntry, ServiceExpanded, ServiceInfoStatusEnum } from '../../sdk'
import { formatCurrency } from '../../utils'

interface Props {
  serviceItems: ServiceExpanded[]
  previousTotal?: number
  newTotal: number
  handleDeferService: (service: ServiceEntry) => void
  handleRecommendService: (service: ServiceEntry) => void
  breakdownPrices?: boolean
}

type RelevantServiceStatuses =
  | typeof ServiceInfoStatusEnum.Authorized
  | typeof ServiceInfoStatusEnum.Recommended
  | typeof ServiceInfoStatusEnum.Deferred

const SERVICE_INFO_STATUS_DESCRIPTIONS: Record<
  RelevantServiceStatuses,
  string
> = {
  [ServiceInfoStatusEnum.Authorized]: 'In Progress',
  [ServiceInfoStatusEnum.Recommended]: 'Recommended',
  [ServiceInfoStatusEnum.Deferred]: 'Deferred'
}

export const CustomerAuthorizationTable = ({
  serviceItems,
  previousTotal,
  newTotal,
  handleDeferService,
  handleRecommendService,
  breakdownPrices // if true, assume serviceItems conforms to interface ServiceItem[] rather than merely ServiceExpanded[]
}: Props) => {
  const serviceInfoCheckHandlers: Record<
    RelevantServiceStatuses,
    ((service: ServiceEntry) => void) | undefined
  > = {
    [ServiceInfoStatusEnum.Authorized]: undefined,
    [ServiceInfoStatusEnum.Recommended]: handleDeferService,
    [ServiceInfoStatusEnum.Deferred]: handleRecommendService
  }
  return (
    <List dense={true}>
      <Divider />
      {Object.keys(SERVICE_INFO_STATUS_DESCRIPTIONS).map((status) => (
        <Fragment key={status}>
          {serviceItems.filter(
            (service) =>
              service.entry!.info!.status ===
              (status as RelevantServiceStatuses)
          ).length > 0 && (
            <ListItem divider>
              <Typography variant="subtitle1">
                {
                  SERVICE_INFO_STATUS_DESCRIPTIONS[
                    status as RelevantServiceStatuses
                  ]
                }
              </Typography>
            </ListItem>
          )}
          {serviceItems
            .filter(
              (service) =>
                service.entry!.info!.status ===
                (status as RelevantServiceStatuses)
            )
            .map((service) => (
              <>
                <ListItem divider key={service.entry!.id!} disablePadding>
                  <ListItemIcon sx={{ justifyContent: 'center' }}>
                    <Checkbox
                      edge="start"
                      checked={true}
                      tabIndex={-1}
                      disableRipple
                      disabled={
                        !serviceInfoCheckHandlers[
                          status as RelevantServiceStatuses
                        ]
                      }
                      onChange={serviceInfoCheckHandlers[
                        status as RelevantServiceStatuses
                      ]?.bind(null, service.entry!)}
                    />
                  </ListItemIcon>
                  <ListItemText sx={{ pr: '20px' }}>
                    {service.entry!.info!.title!}
                  </ListItemText>
                  <ListItemSecondaryAction>
                    <Typography>
                      {formatCurrency(service.cachedRetailPrice || 0, true)}
                    </Typography>
                  </ListItemSecondaryAction>
                </ListItem>
                {breakdownPrices && (
                  <>
                    {(service as ServiceItem).laborOverrides!.length > 0 && (
                      <List dense>
                        <ListSubheader>Labor</ListSubheader>
                        {(service as ServiceItem).laborOverrides!.map(
                          (laborOverride) => (
                            <ListItem key={laborOverride.id}>
                              <ListItemText>
                                {getLaborName(
                                  laborOverride,
                                  service.motorApplications![laborOverride.id!]
                                )}
                              </ListItemText>
                              <ListItemSecondaryAction>
                                <Typography>
                                  {formatCurrency(
                                    service.laborRetailPrices![
                                      laborOverride.id!
                                    ] || 0,
                                    true
                                  )}
                                </Typography>
                              </ListItemSecondaryAction>
                            </ListItem>
                          )
                        )}
                      </List>
                    )}
                    {(service as ServiceItem).partsStandAlone.length +
                      (service as ServiceItem).partsClaimed.length +
                      (service as ServiceItem).partsInCart.length >
                      0 && (
                      <List dense>
                        <ListSubheader>Parts</ListSubheader>
                        {[
                          ...(service as ServiceItem).partsClaimed,
                          ...(service as ServiceItem).partsInCart
                        ].map((part) => (
                          <ListItem key={part.id}>
                            <ListItemText>
                              {displayPartsTechPartName(part, false, false)}
                            </ListItemText>
                            <ListItemSecondaryAction>
                              <Typography>
                                {formatCurrency(part.retailPrice, true)}
                              </Typography>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                        {(service as ServiceItem).partsStandAlone.map(
                          (part) => (
                            <ListItem key={part.id}>
                              <ListItemText>
                                {displayStandAlonePartName(part)}
                              </ListItemText>
                              <ListItemSecondaryAction>
                                <Typography>
                                  {formatCurrency(part.retailPrice, true)}
                                </Typography>
                              </ListItemSecondaryAction>
                            </ListItem>
                          )
                        )}
                      </List>
                    )}
                  </>
                )}
              </>
            ))}
        </Fragment>
      ))}
      <ListItem
        divider
        secondaryAction={
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography>
              Previous: {formatCurrency(previousTotal || 0, true)}
            </Typography>
            <Typography variant="subtitle1">
              Total: {formatCurrency(newTotal, true)}
            </Typography>
          </Stack>
        }>
        <Typography variant="subtitle1">&nbsp;</Typography>
      </ListItem>
    </List>
  )
}
